import { z } from "zod";
import { AUDIT_ACTIONS } from "../audit/actions";
import type { ApiMeta } from "../errors/envelope";
import { SCOPE_TYPES } from "../permissions";
import { cursorQuerySchema } from "./common";

/** Audit trail reads and export (FR-70, SEC-11, SEC-16). */

/**
 * The header the actor filter travels in (SEC-13).
 *
 * The filter takes a user id or an email address. An address in a query string is copied into
 * every ingress and proxy access log, and into browser history, before this process can hash
 * it — and on the CSV export routes the URL is the one a reviewer copies to somebody else. So
 * it is a header on every audit read and export, client and server, and it is named here so
 * the two sides cannot drift.
 */
export const AUDIT_ACTOR_HEADER = "x-skydata-audit-actor";

/**
 * SEC-13: there is deliberately no `actor` in the query. Zod strips an `?actor=` left over
 * from a bookmark or an earlier release, which is why an old link answers an unfiltered page
 * rather than a 400 the reviewer cannot act on.
 */
export const auditEventsQuerySchema = cursorQuerySchema.extend({
  action: z.enum(AUDIT_ACTIONS).optional(),
  entityType: z.string().max(64).optional(),
  entityId: z.string().max(128).optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});
export type AuditEventsQuery = z.infer<typeof auditEventsQuerySchema>;

export const auditEventSchema = z.object({
  id: z.string(),
  occurredAt: z.string(),
  actorUserId: z.uuid().nullable(),
  actorType: z.string(),
  actorRef: z.string(),
  /**
   * Round 4 (2026-09-30): the person's address, joined from `users` when the page is read, as the
   * tenant trail's is (SEC-13: the row stores the user id and the keyed hash, never the address).
   * Null for a system actor or a person no longer in `users`; optional so an older answer parses.
   * The CSV exports carry no address.
   */
  actorEmail: z.string().nullable().optional(),
  correlationId: z.string(),
  source: z.string(),
  action: z.enum(AUDIT_ACTIONS),
  entityType: z.string(),
  entityId: z.string().nullable(),
  entityLabel: z.string().nullable(),
  scopeType: z.enum(SCOPE_TYPES),
  tenantId: z.uuid().nullable(),
  instanceId: z.uuid().nullable(),
  /**
   * The entity snapshots, already redacted at write time (`server/audit/redact.ts`): a
   * secret-shaped key is `***`, a `secret_ref` is reduced to its `{ vaultName, secretName }`
   * pointer and an address is masked. They are carried to the client because the change
   * drawer's whole job is to show what changed, and re-reading the entity cannot answer that
   * for a row written months ago (SEC-11).
   */
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  metadata: z.unknown().nullable(),
  /**
   * The row's position in the tamper-evident chain (SEC-12). A string because it is a
   * bigint, which JSON cannot carry.
   */
  chainSeq: z.string(),
  /**
   * An attempt that answered an error (`metadata.outcome: "failed"`, `server/audit/auditCapture.ts`);
   * nothing durable changed. Optional so an older answer parses (#962).
   */
  failed: z.boolean().optional(),
  /** The refusal's error code for a failed attempt (`metadata.reason`); never the error's text. */
  failureReason: z.string().nullable().optional(),
});
export type AuditEventView = z.infer<typeof auditEventSchema>;

/**
 * The tenant audit trail (product gaps row 9, owner decision 2026-09-28): everything that
 * happened in one tenant, for the customer's own administrators. It merges the platform rows
 * about the tenant (grants made on the tenant screens, instance create and disable, destination
 * and settings changes, whoever made them, Skypoint staff included) with every instance's own
 * trail, newest first.
 *
 * `origin` narrows it: `tenant` keeps the platform rows that concern no one instance; an
 * instance id keeps the rows about that instance from both stores. `actionPrefix` matches the
 * start of the action (`access.` for every access change, `access.grant` for grants only).
 * The actor filter is the `x-skydata-audit-actor` header, as on every audit read (SEC-13).
 */
export const TENANT_AUDIT_ORIGINS = ["tenant", "instance"] as const;

export const tenantAuditEventsQuerySchema = cursorQuerySchema.extend({
  origin: z.union([z.literal("tenant"), z.uuid()]).optional(),
  actionPrefix: z
    .string()
    .regex(/^[a-z][a-z_.]{0,63}$/, "is not the start of an audit action")
    .optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});
export type TenantAuditEventsQuery = z.infer<typeof tenantAuditEventsQuerySchema>;

/**
 * One row of the tenant trail. It carries identifiers only: no `before`, `after` or `metadata`
 * snapshot (SEC-17), so the tenant view says who did what to which entity, and the instance
 * trail stays the place for a change's detail. `actorEmail` is joined from `users` when the page
 * is read (SEC-13: the row stores the user id and a keyed hash, never the address).
 */
export const tenantAuditEventSchema = z.object({
  /** Unique across the merged stores: `platform:<row id>` or `<instance id>:<row id>`. */
  id: z.string(),
  occurredAt: z.string(),
  origin: z.enum(TENANT_AUDIT_ORIGINS),
  /** The instance the row concerns; null for a tenant-level row. */
  instanceId: z.uuid().nullable(),
  /** The tenant's name for a tenant-level row, else the instance's name. */
  originLabel: z.string(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  entityLabel: z.string().nullable(),
  actorUserId: z.uuid().nullable(),
  actorType: z.string(),
  actorRef: z.string(),
  actorEmail: z.string().nullable(),
  correlationId: z.string(),
  source: z.string(),
  scopeType: z.enum(SCOPE_TYPES),
  /** SEC-18: a self-grant at platform scope. */
  breakGlass: z.boolean(),
  /** An attempt that answered an error (`metadata.outcome: "failed"`); nothing durable changed. */
  failed: z.boolean(),
});
export type TenantAuditEventView = z.infer<typeof tenantAuditEventSchema>;

/**
 * An instance whose own trail could not be read for this answer (dev 578de3b: a disabled
 * instance whose schema was dropped outside the product). The other stores' rows are complete; this store's rows are
 * missing until it can be read again, and its platform rows (the create, the disable) are still
 * in the list. `reason` is a code, never the driver's text.
 */
export const tenantAuditUnreadableInstanceSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  /**
   * The instance's own status (round 6 walk W10). The warning no longer reads it: a disabled
   * instance's storage is not removed by the disable (see `storageRemovedAt`). Optional for an
   * answer from before it.
   */
  status: z.enum(["provisioning", "active", "disabled"]).optional(),
  reason: z.literal("store_unavailable"),
  /**
   * Round 7 (backlog "Keep a disabled instance's audit history readable after its schema goes"):
   * what the write-once archive holds for this store's chain (`runner/audit/export.ts`, platform
   * `audit_exports`): the rows of its verified day files and the last day (`YYYY-MM-DD`). Absent
   * when nothing was archived. Counts and a date only (SEC-17).
   */
  archive: z.object({ through: z.string(), rows: z.number().int().nonnegative() }).optional(),
  /**
   * When a PlatformAdmin marked this instance's storage removed (platform 0033,
   * `POST /platform/instances/:id/mark-storage-removed`), else null. Disable never removes
   * storage, so the warning reads this, never `status`. Optional for an answer from before it.
   */
  storageRemovedAt: z.string().nullable().optional(),
  /** The reason typed on the mark (its `instance.mark_storage_removed` audit row), only when marked. */
  storageRemovedReason: z.string().nullable().optional(),
});
export type TenantAuditUnreadableInstance = z.infer<typeof tenantAuditUnreadableInstanceSchema>;

/**
 * The tenant trail's warning: each missing store by name and the reason it is missing. It never
 * names "disabled": disabling an instance never touches its storage (owner, 2026-10-09: the
 * round 6 wording "disabled; its audit store is gone" read as if Disable deleted history).
 *
 * - marked storage removed: "History from S8 live (its storage was removed on Oct 08, 2026:
 *   Session 8 test cleanup) is not included. No write-once archive exists for it. The other rows
 *   are complete." The archive sentence names the archive's rows when one exists.
 * - any other unreadable store: "History from S8 live (its audit store could not be read) is not
 *   included. The other rows are complete."
 */
export function unreadableInstancesWarning(
  unreadable: ReadonlyArray<
    Pick<TenantAuditUnreadableInstance, "name"> & {
      archive?: TenantAuditUnreadableInstance["archive"];
      storageRemovedAt?: string | null;
      storageRemovedReason?: string | null;
    }
  >,
  /** Round 7: how a calendar day reads ("Sep 29, 2026" on the screen); the ISO day by default. */
  formatDay: (isoDay: string) => string = (isoDay) => isoDay,
  /** How the mark's instant reads (the person's display zone on the screen); its UTC day by default. */
  formatRemovedAt: (iso: string) => string = (iso) => iso.slice(0, 10),
): string {
  const described = unreadable.map((instance) => {
    if (!instance.storageRemovedAt) {
      return { name: `${instance.name} (its audit store could not be read)` };
    }
    const reason = instance.storageRemovedReason?.trim();
    const when = formatRemovedAt(instance.storageRemovedAt);
    return {
      name: `${instance.name} (its storage was removed on ${when}${reason ? `: ${reason}` : ""})`,
    };
  });
  const single = unreadable.length === 1;
  // Round 7: where an archive holds the missing history, say how much and to which day; a store
  // marked removed with none says it is not kept anywhere.
  const archived = unreadable
    .map((instance) => {
      if (instance.archive) {
        return ` The write-once archive keeps ${instance.archive.rows.toLocaleString("en-US")} ${
          instance.archive.rows === 1 ? "row" : "rows"
        } of ${instance.name}, through ${formatDay(instance.archive.through)}.`;
      }
      if (instance.storageRemovedAt) {
        return ` No write-once archive exists for ${single ? "it" : instance.name}.`;
      }
      return "";
    })
    .join("");
  return `History from ${unreadableInstanceNames(described)} is not included.${archived} The other rows are complete.`;
}

/**
 * The list's `meta`: the cursor fields every list has, and `unreadableInstances`, present only
 * when at least one store the view needed could not be read (additive: a healthy tenant's answer
 * is what it was before, and an older answer parses).
 */
export interface TenantAuditEventsMeta extends ApiMeta {
  unreadableInstances?: TenantAuditUnreadableInstance[];
}

/** "A", "A and B", "A, B and C": the unreadable stores by name, for the warning and the 409. */
export function unreadableInstanceNames(
  unreadable: ReadonlyArray<Pick<TenantAuditUnreadableInstance, "name">>,
): string {
  const names = unreadable.map((instance) => instance.name);
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
